/**
 * Widget gallery model (Beta-1 plan P3-17) — pure so the filtering is unit-testable.
 * The gallery lists only widgets the viewer may see (isWidgetAllowed: minRole, dashboards,
 * availability hook — the P3-21 matrix plugs in there), grouped by category in display order,
 * with a count of instances already on the layout ("Added").
 */
import { CATEGORY_ORDER, WIDGET_CATEGORIES, isWidgetAllowed } from './widgetRegistry.js';

function norm(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** True when `query` matches the widget's title, description or category label. */
export function widgetMatchesQuery(widget, query) {
  const q = norm(query).trim();
  if (!q) return true;
  const hay = norm(`${widget.title} ${widget.description} ${WIDGET_CATEGORIES[widget.category] || widget.category}`);
  return q.split(/\s+/).every((word) => hay.includes(word));
}

/**
 * Entries for the gallery: `[{ widget, placed }]` filtered and sorted by category then title.
 *   ctx.role / ctx.dashboard / extra keys → isWidgetAllowed
 *   layout   → `placed` = number of instances of that widget already on it
 *   query    → free-text filter; category → one category key or null for all
 */
export function galleryEntries(registry, { layout = null, query = '', category = null, ...ctx } = {}) {
  const counts = new Map();
  for (const it of layout?.items || []) counts.set(it.widgetId, (counts.get(it.widgetId) || 0) + 1);
  return registry
    .list()
    .filter((w) => isWidgetAllowed(w, ctx))
    .filter((w) => !category || w.category === category)
    .filter((w) => widgetMatchesQuery(w, query))
    .map((w) => ({ widget: w, placed: counts.get(w.id) || 0 }));
}

/** Categories that have at least one entry for this viewer, in display order — for the chip row. */
export function galleryCategories(registry, ctx = {}) {
  const present = new Set(registry.list().filter((w) => isWidgetAllowed(w, ctx)).map((w) => w.category));
  return CATEGORY_ORDER.filter((key) => present.has(key)).map((key) => ({ key, label: WIDGET_CATEGORIES[key] }));
}
